import React, { useState } from "react";
import { useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import FancySelect from "../components/FancySelect";
import ToolIcon from "../components/ToolIcon";
import {
  TOOL_LABELS,
  badgeFamily,
  buildSearchText,
  dedupeVisibleApprovalLogs,
  formatLogSummary,
  formatLogWhen,
  getExecutedCount,
  getProposedCount,
  getSkippedCount,
  isActionableApproval,
  isApprovalType,
  isDecisionType,
  isFailureType,
  logOutcome,
  makeNameLookup,
  normalizeLogPagination,
  summaryText,
} from "../lib/auditLogs";
import { toast } from "../lib/toast";
import { dialog } from "../lib/dialog";
import "./AuditLogs.css";

const DEFAULT_LOG_PAGE_SIZE = 500;
const LOG_PAGE_SIZE_OPTIONS = [
  { value: 100, label: "100" },
  { value: 250, label: "250" },
  { value: 500, label: "500" },
];

export default function KPIs() {
  const {
    canManageWorkspace,
    teamMembers,
    pendingApprovals,
    refreshPendingApprovals,
  } = useOutletContext();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // /audit-logs?view=approvals: the "Review approvals" notice lands here.
  const approvalsView = searchParams.get("view") === "approvals";
  const [rows, setRows] = React.useState(null);
  const [logsPage, setLogsPage] = React.useState(1);
  const [logsPageSize, setLogsPageSize] = React.useState(DEFAULT_LOG_PAGE_SIZE);
  const [isLogsLoading, setIsLogsLoading] = React.useState(false);
  const [logsError, setLogsError] = React.useState("");
  const [isClearing, setIsClearing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // --- fetcher now accepts AbortController.signal and disables cache
  const fetchLogs = React.useCallback((signal) => {
    const params = new URLSearchParams({
      page: String(logsPage),
      pageSize: String(logsPageSize),
    });
    setIsLogsLoading(true);
    return fetch(`https://api.bossdrop.ai/api/automation/logs?${params.toString()}`, {
      credentials: "include",
      cache: "no-store",
      signal,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json();
      })
      .then((data) => {
        if (!signal?.aborted) {
          setRows(data);
          setLogsError("");
        }
        return data;
      })
      .catch((err) => {
        if (signal?.aborted || err?.name === "AbortError") return null;
        setLogsError("Couldn't load the audit log.");
        setRows((prev) =>
          prev || {
            logs: [],
            pagination: {
              page: logsPage,
              pageSize: logsPageSize,
              totalCount: 0,
              totalPages: 1,
              hasPrev: false,
              hasNext: false,
              startIndex: 0,
              endIndex: 0,
            },
          },
        );
        return null;
      })
      .finally(() => {
        if (!signal?.aborted) setIsLogsLoading(false);
      });
  }, [logsPage, logsPageSize]);
  const refreshLogs = React.useCallback(() => {
    const ac = new AbortController();
    return fetchLogs(ac.signal);
  }, [fetchLogs]);
  // After an approve or deny: this page and the hub-wide pending count.
  const refreshAfterDecision = React.useCallback(async () => {
    await Promise.all([
      refreshLogs(),
      Promise.resolve(refreshPendingApprovals?.()).catch(() => null),
    ]);
  }, [refreshLogs, refreshPendingApprovals]);

  // --- POLLING LOOP (live feed)
  React.useEffect(() => {
    let alive = true;
    let timer = null;
    let backoffMs = 4000; // base interval
    let controller = new AbortController();

    const tick = async () => {
      // cancel any in-flight request before starting a new one
      controller.abort();
      controller = new AbortController();
      try {
        await fetchLogs(controller.signal);
        backoffMs = 4000; // reset on success
      } catch {
        // exponential backoff on errors up to 30s
        backoffMs = Math.min(Math.round(backoffMs * 1.75), 30000);
      }
      if (!alive) return;
      const visible = document.visibilityState === "visible";
      const delay = visible ? backoffMs : Math.max(backoffMs, 20000);
      timer = setTimeout(tick, delay);
    };

    // kick off immediately
    tick();

    const onVis = () => {
      if (document.visibilityState === "visible") {
        // when user returns, refresh immediately
        clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchLogs]);

  React.useEffect(() => {
    const pagination = normalizeLogPagination(rows, logsPage, logsPageSize);
    if (logsPage > pagination.totalPages) {
      setLogsPage(pagination.totalPages);
    }
  }, [rows, logsPage, logsPageSize]);

  // Fetch via XHR (not a plain link) so the session cookie is sent
  // cross-origin, then hand the browser a blob to download.
  const handleExportLogs = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const r = await fetch(
        "https://api.bossdrop.ai/api/automation/logs/export?days=90",
        { credentials: "include", cache: "no-store" },
      );
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bossdrop-audit-log-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't export the audit log. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearLogs = async () => {
    if (!canManageWorkspace) return;
    const confirmed = await dialog.confirm({
      title: "Clear all automation logs for your account?",
      confirmLabel: "Clear logs",
      danger: true,
    });
    if (!confirmed) return;
    setIsClearing(true);
    try {
      const r = await fetch(
        "https://api.bossdrop.ai/api/automation/deletelogs",
        { method: "DELETE", credentials: "include" },
      );
      if (!r.ok) throw new Error(await r.text());
      if (logsPage !== 1) setLogsPage(1);
      else {
        const ac = new AbortController();
        await fetchLogs(ac.signal); // immediate refresh after clear
      }
      refreshPendingApprovals?.();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't clear the logs. Please try again.");
    } finally {
      setIsClearing(false);
    }
  };

  const nameFor =React.useMemo(() => makeNameLookup(teamMembers), [teamMembers]);

  // Leaving the approvals view through the filter drops ?view=approvals, so
  // a reload shows what is on screen rather than snapping back to it.
  const onOutcomeFilterChange = React.useCallback(
    (value) => {
      if (approvalsView && value !== "pending") {
        const next = new URLSearchParams(searchParams);
        next.delete("view");
        setSearchParams(next, { replace: true });
      }
    },
    [approvalsView, searchParams, setSearchParams],
  );

  return (
    <section className="panel auditLogs">
      <header className="alHeader">
        <div className="alHeader__titles">
          <h2>Audit Logs</h2>
          <p>Every action BossDrop took, skipped or queued for approval.</p>
        </div>
        <div className="alHeader__actions">
          {!canManageWorkspace ? (
            <span className="alReadOnly" title="Only a workspace admin can approve, deny or clear logs">
              Read-only view
            </span>
          ) : null}
          <button
            className="btn alBtn"
            type="button"
            onClick={handleExportLogs}
            disabled={isExporting}
            title="Download the last 90 days of audit log as CSV"
          >
            <DownloadIcon />
            {isExporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            className="btn alBtn alBtn--danger"
            type="button"
            onClick={handleClearLogs}
            disabled={isClearing || !canManageWorkspace}
          >
            {isClearing ? "Clearing…" : "Clear logs"}
          </button>
        </div>
      </header>

      <AutomationLogList
        rows={rows}
        canManageWorkspace={canManageWorkspace}
        onRefresh={refreshAfterDecision}
        onRetry={refreshLogs}
        logsError={logsError}
        page={logsPage}
        pageSize={logsPageSize}
        isLogsLoading={isLogsLoading}
        onPageChange={setLogsPage}
        onPageSizeChange={(nextPageSize) => {
          const numericSize = Number(nextPageSize) || DEFAULT_LOG_PAGE_SIZE;
          setLogsPageSize(numericSize);
          setLogsPage(1);
        }}
        nameFor={nameFor}
        pendingApprovals={pendingApprovals?.approvals}
        pendingComplete={pendingApprovals?.complete !== false}
        approvalsView={approvalsView}
        focusKey={approvalsView ? location.key || "approvals" : ""}
        onOutcomeFilterChange={onOutcomeFilterChange}
        emptyHint="Generate an exec rewrite and schedule via Scheduler to start populating KPIs."
      />
    </section>
  );
}

const OUTCOME_OPTIONS = [
  { value: "all", label: "All outcomes" },
  { value: "executed", label: "Executed actions" },
  { value: "skipped", label: "Skipped actions" },
  { value: "failed", label: "Failed/error actions" },
  { value: "decision", label: "Decision events" },
  { value: "approval", label: "Approval queue" },
  { value: "pending", label: "Waiting for approval" },
];

function logKey(l, i) {
  return l?.id != null ? `id-${l.id}` : `${l?.ts}-${l?.type}-${i}`;
}

// The current page, plus (in the waiting-for-approval view) every pending
// approval the hub knows about, so one older than this page still shows.
function withPendingApprovals(logs, pending) {
  const extra = (Array.isArray(pending) ? pending : []).filter(
    (p) => p?.id == null || !logs.some((l) => l?.id === p.id),
  );
  if (!extra.length) return logs;
  const merged = [...logs, ...extra].sort(
    (a, b) => (Date.parse(b?.ts) || 0) - (Date.parse(a?.ts) || 0),
  );
  return dedupeVisibleApprovalLogs(merged);
}

export function AutomationLogList({
  rows,
  canManageWorkspace,
  onRefresh,
  onRetry,
  logsError = "",
  page,
  pageSize,
  isLogsLoading,
  onPageChange,
  onPageSizeChange,
  nameFor = (v) => v,
  pendingApprovals = [],
  pendingComplete = true,
  approvalsView = false,
  focusKey = "",
  onOutcomeFilterChange,
  emptyHint = "",
  initialOpenIds = [],
}) {
  const pageLogs = React.useMemo(
    () => dedupeVisibleApprovalLogs(Array.isArray(rows?.logs) ? rows.logs : []),
    [rows],
  );
  const pagination = React.useMemo(
    () => normalizeLogPagination(rows, page, pageSize),
    [rows, page, pageSize],
  );

  const [query, setQuery] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [outcomeFilter, setOutcomeFilterState] = React.useState(
    approvalsView ? "pending" : "all",
  );
  const [onlyHasSkipped, setOnlyHasSkipped] = React.useState(false);
  const [onlyNoProposed, setOnlyNoProposed] = React.useState(false);
  const [onlyNoExecuted, setOnlyNoExecuted] = React.useState(false);
  const [pageInput, setPageInput] = React.useState(String(pagination.page));
  const [openKeys, setOpenKeys] = React.useState(() => new Set(initialOpenIds));
  const [spotlightKey, setSpotlightKey] = React.useState("");
  const focusedRef = React.useRef("");

  const setOutcomeFilter = React.useCallback(
    (value) => {
      setOutcomeFilterState(value);
      onOutcomeFilterChange?.(value);
    },
    [onOutcomeFilterChange],
  );

  // Arriving on ?view=approvals selects the waiting rows; leaving it by a
  // plain /audit-logs link (same page, still mounted) goes back to all.
  const wasApprovalsViewRef = React.useRef(approvalsView);
  React.useEffect(() => {
    if (approvalsView) setOutcomeFilterState("pending");
    else if (wasApprovalsViewRef.current) {
      setOutcomeFilterState((cur) => (cur === "pending" ? "all" : cur));
    }
    wasApprovalsViewRef.current = approvalsView;
  }, [approvalsView, focusKey]);

  React.useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  const logs = React.useMemo(
    () =>
      outcomeFilter === "pending"
        ? withPendingApprovals(pageLogs, pendingApprovals)
        : pageLogs,
    [outcomeFilter, pageLogs, pendingApprovals],
  );

  // Everything a row shows, worked out once per refresh.
  const items = React.useMemo(
    () =>
      logs.map((l, i) => {
        const summary = formatLogSummary(l, { nameFor });
        const outcome = logOutcome(l);
        const toolNames = summary.tools.map((t) => TOOL_LABELS[t] || t).join(" ");
        return {
          key: logKey(l, i),
          log: l,
          type: String(l?.type || "unknown"),
          family: badgeFamily(l?.type),
          when: formatLogWhen(l?.ts),
          summary,
          outcome,
          outcomeBy: outcome.by ? nameFor(outcome.by) : "",
          actionable: isActionableApproval(l),
          search: buildSearchText(
            l,
            `${summaryText(summary)} ${summary.context} ${toolNames} ${outcome.label}`,
          ),
        };
      }),
    [logs, nameFor],
  );

  const typeFilterOptions = React.useMemo(() => {
    const uniq = Array.from(
      new Set(logs.map((l) => String(l?.type || "")).filter(Boolean)),
    );
    uniq.sort((a, b) => a.localeCompare(b));
    return [
      { value: "all", label: "All types" },
      ...uniq.map((t) => ({ value: t, label: t.replace(/_/g, " ") })),
    ];
  }, [logs]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(({ log: l, type, search, actionable }) => {
      const proposed = getProposedCount(l);
      const executed = getExecutedCount(l);
      const skipped = getSkippedCount(l);

      if (typeFilter !== "all" && type !== typeFilter) return false;

      if (outcomeFilter === "executed" && !(Number(executed) > 0)) return false;
      if (outcomeFilter === "skipped" && !(Number(skipped) > 0)) return false;
      if (outcomeFilter === "failed" && !isFailureType(type)) return false;
      if (outcomeFilter === "decision" && !isDecisionType(type)) return false;
      if (outcomeFilter === "approval" && !isApprovalType(type)) return false;
      if (outcomeFilter === "pending" && !actionable) return false;

      if (onlyHasSkipped && !(Number(skipped) > 0)) return false;
      if (onlyNoProposed && proposed !== 0) return false;
      if (onlyNoExecuted && executed !== 0) return false;

      if (q && !search.includes(q)) return false;
      return true;
    });
  }, [
    items,
    query,
    typeFilter,
    outcomeFilter,
    onlyHasSkipped,
    onlyNoProposed,
    onlyNoExecuted,
  ]);

  // Arriving from "Review approvals": scroll the first waiting row into view
  // and light it up briefly. Once per arrival.
  React.useEffect(() => {
    if (!focusKey || focusedRef.current === focusKey || !rows) return;
    const first = filtered.find((it) => it.actionable);
    if (!first) return;
    focusedRef.current = focusKey;
    setSpotlightKey(first.key);
    // After this render commits, so the row exists to scroll to.
    setTimeout(() => {
      const el = document.getElementById(`al-row-${first.key}`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      el?.querySelector?.(".alMini--approve:not(:disabled)")?.focus?.({
        preventScroll: true,
      });
    }, 0);
  }, [focusKey, filtered, rows]);

  React.useEffect(() => {
    if (!spotlightKey) return undefined;
    const t = setTimeout(() => setSpotlightKey(""), 4000);
    return () => clearTimeout(t);
  }, [spotlightKey]);

  const toggleOpen = React.useCallback((key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const commitPageInput = React.useCallback(() => {
    const requested = Number(pageInput);
    if (!Number.isFinite(requested)) {
      setPageInput(String(pagination.page));
      return;
    }
    const nextPage = Math.min(
      pagination.totalPages,
      Math.max(1, Math.floor(requested)),
    );
    setPageInput(String(nextPage));
    if (nextPage !== pagination.page) onPageChange?.(nextPage);
  }, [pageInput, pagination.page, pagination.totalPages, onPageChange]);

  const hasActiveFilters = Boolean(
    query.trim() ||
      typeFilter !== "all" ||
      outcomeFilter !== "all" ||
      onlyHasSkipped ||
      onlyNoProposed ||
      onlyNoExecuted,
  );

  const hasLogs = rows !== null && logs.length > 0;
  const showFilters = hasLogs || hasActiveFilters;
  const showingLabel = `Showing ${filtered.length} of ${logs.length}${
    pagination.totalPages > 1 && outcomeFilter !== "pending" ? " on this page" : ""
  }`;

  return (
    <div className="alList">
      {showFilters ? (
        <div className="alFilters">
          <div className="alFilters__row">
            <label className="alSearch">
              <SearchIcon />
              <span className="alVisuallyHidden">Search logs</span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type, title, assignee, reason…"
              />
            </label>

            <div className="alSelect alSelect--outcome">
              <span className="alSelect__prefix" aria-hidden="true">
                Outcome
              </span>
              <FancySelect
                value={outcomeFilter}
                onChange={setOutcomeFilter}
                options={OUTCOME_OPTIONS}
                placeholder="All outcomes"
                ariaLabel="Filter by action outcome"
                usePortal={true}
              />
            </div>

            <div className="alSelect alSelect--type">
              <span className="alSelect__prefix" aria-hidden="true">
                Type
              </span>
              <FancySelect
                value={typeFilter}
                onChange={setTypeFilter}
                options={typeFilterOptions}
                placeholder="All types"
                ariaLabel="Filter by event type"
                usePortal={true}
              />
            </div>
          </div>

          <div className="alFilters__checks">
            <label className="alCheck">
              <input
                type="checkbox"
                className="bd-check"
                checked={onlyHasSkipped}
                onChange={(e) => setOnlyHasSkipped(e.target.checked)}
              />
              Has skipped items
            </label>
            <label className="alCheck">
              <input
                type="checkbox"
                className="bd-check"
                checked={onlyNoProposed}
                onChange={(e) => setOnlyNoProposed(e.target.checked)}
              />
              No proposed actions
            </label>
            <label className="alCheck">
              <input
                type="checkbox"
                className="bd-check"
                checked={onlyNoExecuted}
                onChange={(e) => setOnlyNoExecuted(e.target.checked)}
              />
              No executed actions
            </label>
            <button
              className="alLinkBtn"
              type="button"
              onClick={() => {
                setQuery("");
                setTypeFilter("all");
                setOutcomeFilter("all");
                setOnlyHasSkipped(false);
                setOnlyNoProposed(false);
                setOnlyNoExecuted(false);
              }}
              disabled={!hasActiveFilters}
            >
              Clear filters
            </button>
            <span className="alCount" aria-live="polite">
              {showingLabel}
            </span>
          </div>
        </div>
      ) : null}

      {logsError ? (
        <div className="alNotice alNotice--error" role="alert">
          <span>
            {logsError}{" "}
            {rows?.logs?.length
              ? "Showing the last copy that loaded. Retrying automatically."
              : "Retrying automatically."}
          </span>
          <button className="alLinkBtn" type="button" onClick={() => onRetry?.()}>
            Try again
          </button>
        </div>
      ) : null}

      {outcomeFilter === "pending" && !pendingComplete && pagination.totalPages > 1 ? (
        <p className="alNote">
          Waiting approvals older than the newest page of the log may be on later pages.
        </p>
      ) : null}

      <div className="alTable">
        <div className="alThead" aria-hidden="true">
          <span className="alThead__time">Time</span>
          <span className="alThead__type">Type</span>
          <span>Summary</span>
          <span>Outcome</span>
          <span />
        </div>

        {!rows ? (
          <div className="alState">
            <span className="alSpinner" aria-hidden="true" />
            Loading logs…
          </div>
        ) : !logs.length ? (
          logsError ? (
            <div className="alState">
              <strong>The audit log couldn&rsquo;t be loaded.</strong>
              <span>Check your connection; this page retries on its own.</span>
            </div>
          ) : (
            <div className="alState">
              <strong>
                {outcomeFilter === "pending"
                  ? "Nothing is waiting for approval."
                  : "No automation activity yet."}
              </strong>
              {emptyHint && outcomeFilter !== "pending" ? <span>{emptyHint}</span> : null}
            </div>
          )
        ) : !filtered.length ? (
          <div className="alState">
            <strong>
              {outcomeFilter === "pending"
                ? "Nothing is waiting for approval."
                : "No logs on this page match the selected filters."}
            </strong>
          </div>
        ) : (
          <ol className="alRows">
            {filtered.map((it) => (
              <LogRow
                key={it.key}
                item={it}
                open={openKeys.has(it.key)}
                onToggle={toggleOpen}
                spotlight={spotlightKey === it.key}
                canManageWorkspace={canManageWorkspace}
                onRefresh={onRefresh}
              />
            ))}
          </ol>
        )}
      </div>

      <LogPaginationBar
        pagination={pagination}
        pageInput={pageInput}
        setPageInput={setPageInput}
        onCommitPageInput={commitPageInput}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        isLogsLoading={isLogsLoading}
      />
    </div>
  );
}

// A link to the task or meeting a row is about (formatLogSummary href).
function SummaryLink({ href, children }) {
  return (
    <a className="alSum__link" href={href} target="_blank" rel="noopener noreferrer" title="Opens in a new tab">
      {children}
    </a>
  );
}

// The context line, with the task's name linked on a row whose bold name is
// a person (summary.hrefLabel).
function ContextWithLink({ summary }) {
  const { context, href, hrefLabel } = summary;
  const i = href && hrefLabel ? context.indexOf(hrefLabel) : -1;
  if (i < 0) return context;
  return (
    <>
      {context.slice(0, i)}
      <SummaryLink href={href}>{hrefLabel}</SummaryLink>
      {context.slice(i + hrefLabel.length)}
    </>
  );
}

function LogRow({ item, open, onToggle, spotlight, canManageWorkspace, onRefresh }) {
  const { key, log, type, family, when, summary, outcome, outcomeBy, actionable } = item;
  const detailsId = `al-details-${key}`;
  const line = summaryText(summary);
  return (
    <li
      id={`al-row-${key}`}
      className={`alRow${open ? " alRow--open" : ""}${
        actionable ? " alRow--pending" : ""
      }${spotlight ? " alRow--spotlight" : ""}`}
    >
      <div className="alRow__grid">
        <div className="alWhen" title={when.ago ? `${when.full} (${when.ago})` : when.full}>
          <b>{when.time}</b>
          <span>{when.date}</span>
        </div>

        <div className="alType">
          <span className={`alBadge alBadge--${family}`} title={type}>
            {type.replace(/_/g, " ")}
          </span>
        </div>

        <div className="alSum">
          <p className="alSum__line" title={line}>
            {summary.lead ? `${summary.lead} ` : ""}
            {summary.entity ? (
              summary.href && !summary.hrefLabel ? (
                // The task or meeting itself, from the log's own link.
                <SummaryLink href={summary.href}>
                  <strong>{summary.entity}</strong>
                </SummaryLink>
              ) : (
                <strong>{summary.entity}</strong>
              )
            ) : null}
            {summary.tail ? ` ${summary.tail}` : ""}
          </p>
          {summary.tools.length || summary.context ? (
            <div className="alSum__via">
              {summary.tools.map((t) => (
                <ToolIcon key={t} tool={t} />
              ))}
              {summary.context ? (
                <span className="alSum__context" title={summary.context}>
                  <ContextWithLink summary={summary} />
                </span>
              ) : null}
            </div>
          ) : null}
          {summary.why ? (
            <p className="alSum__why">
              <b>Why:</b> {summary.why}
            </p>
          ) : null}
        </div>

        <div className="alOut">
          <OutcomePill outcome={outcome} />
          {outcomeBy ? (
            <small className="alOut__by">
              by <b>{outcomeBy}</b>
            </small>
          ) : null}
          {actionable ? (
            <ApprovalActions
              log={log}
              canManageWorkspace={canManageWorkspace}
              onRefresh={onRefresh}
            />
          ) : null}
        </div>

        <div className="alMore">
          <button
            type="button"
            className="alDetailsBtn"
            aria-expanded={open}
            aria-controls={detailsId}
            onClick={() => onToggle(key)}
            title={open ? "Hide the logged JSON" : "Show the logged JSON"}
          >
            <span className="alDetailsBtn__label">Details</span>
            <ChevronIcon />
          </button>
        </div>
      </div>

      {open ? <LogDetails id={detailsId} log={log} /> : null}
    </li>
  );
}

function LogDetails({ id, log }) {
  const [copied, setCopied] = React.useState(false);
  const full = React.useMemo(() => {
    try {
      return JSON.stringify(log, null, 2);
    } catch {
      return String(log);
    }
  }, [log]);
  return (
    <div className="alDetails" id={id}>
      <div className="alDetails__bar">
        <span>Logged JSON</span>
        <button
          className="alLinkBtn"
          type="button"
          onClick={() => {
            try {
              navigator.clipboard.writeText(full);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {}
          }}
        >
          {copied ? "Copied" : "Copy JSON"}
        </button>
      </div>
      <pre className="alDetails__json" tabIndex={0}>
        {full}
      </pre>
    </div>
  );
}

function OutcomePill({ outcome }) {
  return (
    <span className={`alPill alPill--${outcome.tone}`}>
      <OutcomeIcon icon={outcome.icon} />
      {outcome.label}
    </span>
  );
}

function OutcomeIcon({ icon }) {
  if (icon === "dot") return <i className="alPill__dot" aria-hidden="true" />;
  const paths = {
    check: <path d="M2.5 6.3l2.3 2.2 4.7-5" strokeWidth="1.6" />,
    clock: (
      <>
        <circle cx="6" cy="6" r="4.3" />
        <path d="M6 3.7V6l1.6 1" />
      </>
    ),
    skip: (
      <>
        <circle cx="6" cy="6" r="4.3" />
        <path d="m3 9 6-6" />
      </>
    ),
    x: <path d="m3.4 3.4 5.2 5.2m0-5.2-5.2 5.2" strokeWidth="1.5" />,
    alert: (
      <>
        <path d="M6 1.8 10.6 10H1.4Z" />
        <path d="M6 5v2.1M6 8.6v.1" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths[icon] || paths.clock}
    </svg>
  );
}

// Re-reads after an approve the server is still running (202 { running }).
const RUNNING_REFRESH_DELAYS_MS = [15_000, 45_000, 90_000, 180_000, 330_000];

function ApprovalActions({ log, canManageWorkspace, onRefresh }) {
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const id = log?.id;
  const disabled = !canManageWorkspace || !!busy || !id;

  const submit = async (kind) => {
    if (!id || busy) return;
    setBusy(kind);
    setError("");
    try {
      const r = await fetch(
        `https://api.bossdrop.ai/api/automation/approvals/${id}/${kind}`,
        { method: "POST", credentials: "include" },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data?.ok === false) {
        await onRefresh?.();
        throw new Error(data?.error || `${kind}_failed`);
      }
      await onRefresh?.();
      // Still running (the server answers 202 while the approval waits its
      // turn behind other runs): look again so "Approved, running" moves on
      // to its outcome without a reload.
      if (data?.running) {
        RUNNING_REFRESH_DELAYS_MS.forEach((ms) => {
          setTimeout(() => {
            onRefresh?.();
          }, ms);
        });
      }
    } catch (e) {
      setError(String(e?.message || e || "Action failed").replace(/_/g, " "));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="alApprove">
      <div className="alApprove__buttons">
        <button
          type="button"
          className="alMini alMini--approve"
          onClick={() => submit("approve")}
          disabled={disabled}
          title={canManageWorkspace ? "Approve and run this action" : "Workspace admin approval required"}
        >
          {busy === "approve" ? "Approving…" : "Approve"}
        </button>
        <button
          type="button"
          className="alMini alMini--deny"
          onClick={() => submit("deny")}
          disabled={disabled}
          title={canManageWorkspace ? "Deny this action" : "Workspace admin approval required"}
        >
          {busy === "deny" ? "Denying…" : "Deny"}
        </button>
      </div>
      {!canManageWorkspace ? (
        <small className="alApprove__note">Workspace admin approval required.</small>
      ) : null}
      {error ? (
        <small className="alApprove__error" role="alert">
          {error}
        </small>
      ) : null}
    </div>
  );
}

function LogPaginationBar({
  pagination,
  pageInput,
  setPageInput,
  onCommitPageInput,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLogsLoading,
}) {
  const canGoPrev = pagination.hasPrev;
  const canGoNext = pagination.hasNext;
  const rangeLabel =
    pagination.totalCount > 0
      ? `${pagination.startIndex}–${pagination.endIndex} of ${pagination.totalCount.toLocaleString()} logs`
      : "No logs yet";

  return (
    <div className="alPager">
      <div className="alPager__left">
        <div className="alPager__sizes" role="group" aria-label="Rows per page">
          <span className="alPager__label">Rows per page</span>
          <div className="alSeg">
            {LOG_PAGE_SIZE_OPTIONS.map(({ value, label }) => {
              const active = pageSize === value;
              return (
                <button
                  key={value}
                  type="button"
                  className={`alSeg__btn${active ? " is-active" : ""}`}
                  onClick={() => onPageSizeChange?.(value)}
                  disabled={isLogsLoading && active}
                  aria-pressed={active}
                  aria-label={`Show ${value} logs per page`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <span className="alPager__range">
          {rangeLabel}
          {isLogsLoading ? (
            <span className="alPager__refreshing" title="Refreshing">
              <span className="alSpinner" aria-hidden="true" />
              <span className="alVisuallyHidden">Refreshing</span>
            </span>
          ) : null}
        </span>
      </div>

      <div className="alPager__right">
        <button
          type="button"
          className="alPagerBtn"
          onClick={() => onPageChange?.(1)}
          disabled={!canGoPrev}
          title="First page"
          aria-label="First page"
        >
          «
        </button>
        <button
          type="button"
          className="alPagerBtn"
          onClick={() => onPageChange?.(pagination.page - 1)}
          disabled={!canGoPrev}
          title="Previous page"
        >
          ‹ Prev
        </button>
        <span className="alPager__page">
          Page
          <input
            type="number"
            min={1}
            max={pagination.totalPages}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={onCommitPageInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onCommitPageInput();
              }
            }}
            aria-label="Audit log page number"
          />
          of {pagination.totalPages.toLocaleString()}
        </span>
        <button
          type="button"
          className="alPagerBtn"
          onClick={() => onPageChange?.(pagination.page + 1)}
          disabled={!canGoNext}
          title="Next page"
        >
          Next ›
        </button>
        <button
          type="button"
          className="alPagerBtn"
          onClick={() => onPageChange?.(pagination.totalPages)}
          disabled={!canGoNext}
          title="Last page"
          aria-label="Last page"
        >
          »
        </button>
      </div>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg className="alIcon" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M6 1.8v5.6M3.6 5.2 6 7.6l2.4-2.4M2.2 9.4v.8c0 .4.3.7.7.7h6.2c.4 0 .7-.3.7-.7v-.8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="alIcon" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
      <circle cx="5.2" cy="5.2" r="3.4" stroke="currentColor" strokeWidth="1.3" />
      <path d="m7.8 7.8 2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="alIcon alChevron" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m3.5 4.8 2.5 2.5 2.5-2.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
